import routes from "./routes.json";

export const SITE_URL = "https://mailsnappy.com";
export const SITE_NAME = "Mailsnappy";
export const DEFAULT_ROBOTS = "index, follow";
export const SOCIAL_IMAGE = `${SITE_URL}/favicon-512x512.png`;

const routeByPath = new Map(routes.map((route) => [route.path, route]));
const sitemapRoutes = routes.filter((route) => route.sitemap);

function stripTrailingSlash(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "");
}

function titleFromSlugPath(pathname) {
  const slug = String(pathname || "")
    .split("/")
    .filter(Boolean)
    .pop() || "";
  const withoutId = slug.replace(/-\d+$/, "");
  const words = withoutId.split("-").filter(Boolean);

  if (!words.length) return "";

  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function canonicalPath(pathname) {
  return stripTrailingSlash(String(pathname || "/").split("?")[0].split("#")[0]) || "/";
}

export function canonicalUrl(pathname) {
  const path = canonicalPath(pathname);
  return `${SITE_URL}${path === "/" ? "/" : path}`;
}

function titleFromCategorySlug(pathname) {
  const parts = String(pathname || "").split("/").filter(Boolean);
  const slug = parts[parts.length - 1] || "";
  const withoutId = slug.replace(/-[^-]+$/, "");
  return withoutId
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function publicRoutes() {
  return sitemapRoutes;
}

export function metaForPath(pathname) {
  const path = canonicalPath(pathname);
  if (routeByPath.has(path)) {
    const route = routeByPath.get(path);
    return { ...route, breadcrumbs: route.breadcrumbs || breadcrumbItems(path, route.h1 || route.title) };
  }

  if (path.startsWith("/templates/category/")) {
    const categoryName = titleFromCategorySlug(path) || "Email Template";
    return {
      path,
      title: `${categoryName} Email Templates | Mailsnappy`,
      description: `Browse ${categoryName.toLowerCase()} email templates from Mailsnappy. Preview responsive HTML designs for newsletters, promotions, announcements, and campaigns.`,
      h1: `${categoryName} email templates`,
      robots: DEFAULT_ROBOTS,
      breadcrumbs: breadcrumbItems(path, categoryName),
    };
  }

  if (path.startsWith("/templates/")) {
    const templateName = titleFromSlugPath(path) || "Email Template Preview";
    return {
      path,
      title: `${templateName} Email Template | Mailsnappy`,
      description: `Preview and download the ${templateName} email template for responsive newsletters, promotions, announcements, and marketing campaigns.`,
      h1: templateName,
      robots: DEFAULT_ROBOTS,
      breadcrumbs: breadcrumbItems(path, templateName),
    };
  }

  if (path.startsWith("/blog/category/")) {
    const categoryName = titleFromCategorySlug(path) || "Email Marketing";
    return {
      path,
      title: `${categoryName} Blog Articles | Mailsnappy`,
      description: `Read Mailsnappy blog articles about ${categoryName.toLowerCase()}, email templates, campaign planning, automation, and email marketing performance.`,
      h1: `${categoryName} blog articles`,
      robots: DEFAULT_ROBOTS,
      breadcrumbs: breadcrumbItems(path, categoryName),
    };
  }

  if (path.startsWith("/blog/")) {
    return {
      path,
      title: "Blog Not Indexed | Mailsnappy",
      description: "This Mailsnappy blog route is handled by the application and is excluded from this SEO sitemap task.",
      h1: "Blog",
      robots: "noindex, follow",
      breadcrumbs: breadcrumbItems("/blog", "Blog"),
    };
  }

  return {
    path,
    title: "Page Not Found | Mailsnappy",
    description: "The page you requested could not be found on Mailsnappy.",
    h1: "Page not found",
    robots: "noindex, nofollow",
    breadcrumbs: [],
  };
}

export function breadcrumbItems(pathname, label) {
  const path = canonicalPath(pathname);
  if (path === "/") return [];

  const items = [{ name: "Home", path: "/" }];
  const route = routeByPath.get(path);
  const pageName = label || route?.h1 || route?.title || "Page";

  if (["/automation", "/segmentation", "/analytics", "/grow-audience", "/email-builder", "/audience-tools", "/automation-builder", "/segmentation-tools"].includes(path)) {
    items.push({ name: "Features", path: "/features" });
  } else if (path === "/alternatives") {
    items.push({ name: pageName, path });
    return items;
  } else if (path.startsWith("/templates/")) {
    items.push({ name: "Email templates", path: "/templates" });
  } else if (path.startsWith("/blog/category/")) {
    items.push({ name: "Blog", path: "/blog" });
  } else if (["/gettingstarted", "/GuideSubjectLines", "/GuideEffectiveEmail", "/GuidePersonalising", "/GuideScheduling", "/GuideMeasureSuccess"].includes(path)) {
    items.push({ name: "Email marketing guide", path: "/guide" });
  } else if (["/mailchimp-alternative", "/brevo-alternative", "/constant-contact-alternative", "/mailerlite-alternative", "/klaviyo-alternative"].includes(path)) {
    items.push({ name: "Alternatives", path: "/alternatives" });
  }

  items.push({ name: pageName, path });
  return items;
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/favicon-512x512.png`,
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function webApplicationSchema(pageMeta) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "@id": `${canonicalUrl(pageMeta.path)}#webapplication`,
    name: pageMeta.title.replace(/\s\|\sMailsnappy.*/, ""),
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: canonicalUrl(pageMeta.path),
    description: pageMeta.description,
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function faqPageSchema(pageMeta) {
  if (!Array.isArray(pageMeta.faqs) || !pageMeta.faqs.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pageMeta.faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function breadcrumbSchema(pageMeta) {
  const items = pageMeta.breadcrumbs || breadcrumbItems(pageMeta.path, pageMeta.h1 || pageMeta.title);
  if (!items.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: canonicalUrl(item.path),
    })),
  };
}

export function schemaForMeta(pageMeta) {
  const requested = pageMeta.schema || [];
  const schemas = requested.map((type) => {
    if (type === "Organization") return organizationSchema();
    if (type === "WebSite") return websiteSchema();
    if (type === "WebApplication" || type === "SoftwareApplication") return webApplicationSchema(pageMeta);
    if (type === "FAQPage") return faqPageSchema(pageMeta);
    return null;
  }).filter(Boolean);

  const breadcrumbs = breadcrumbSchema(pageMeta);
  if (breadcrumbs) schemas.push(breadcrumbs);
  return schemas;
}

function setMetaBySelector(selector, create, content) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setMetaName(name, content) {
  setMetaBySelector(`meta[name="${name}"]`, () => {
    const element = document.createElement("meta");
    element.setAttribute("name", name);
    return element;
  }, content);
}

function setMetaProperty(property, content) {
  setMetaBySelector(`meta[property="${property}"]`, () => {
    const element = document.createElement("meta");
    element.setAttribute("property", property);
    return element;
  }, content);
}

function setLinkRel(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

export function applySeo(pageMeta) {
  const url = canonicalUrl(pageMeta.path);
  const robots = pageMeta.robots || DEFAULT_ROBOTS;

  document.title = pageMeta.title;
  setMetaName("description", pageMeta.description);
  setMetaName("robots", robots);
  setMetaName("twitter:card", "summary_large_image");
  setMetaName("twitter:title", pageMeta.twitterTitle || pageMeta.title);
  setMetaName("twitter:description", pageMeta.twitterDescription || pageMeta.description);
  setMetaName("twitter:image", pageMeta.image || SOCIAL_IMAGE);
  setMetaProperty("og:type", "website");
  setMetaProperty("og:site_name", SITE_NAME);
  setMetaProperty("og:title", pageMeta.ogTitle || pageMeta.title);
  setMetaProperty("og:description", pageMeta.ogDescription || pageMeta.description);
  setMetaProperty("og:url", url);
  setMetaProperty("og:image", pageMeta.image || SOCIAL_IMAGE);
  setMetaProperty("og:image:width", "512");
  setMetaProperty("og:image:height", "512");
  setLinkRel("canonical", url);

  document.head.querySelectorAll('script[type="application/ld+json"][data-seo-schema="true"]').forEach((node) => node.remove());
  schemaForMeta(pageMeta).forEach((schema) => {
    const element = document.createElement("script");
    element.type = "application/ld+json";
    element.dataset.seoSchema = "true";
    element.textContent = JSON.stringify(schema);
    document.head.appendChild(element);
  });
}
